import type { Database } from 'sql.js';
import {
  queryAll,
  type SqlJsBind,
} from '@core/infrastructure/driven/sqljs/sqljsSqlitePrimitives.ts';
import { foldParticipanteBusquedaText } from '../domain/participanteBusquedaText.ts';

/** Writable participant catalog columns kept in sync with `nombre_normalizado`. */
export type ParticipanteCatalogUpsertFields = {
  publicId: string;
  tipoDocumentoId: number;
  documento: string;
  nombre: string;
  telefono: string;
  celular: string;
  comunaId: number;
  tipoBonoId: number | null;
  estado: number;
};

/** Normalized participant name used for accent-insensitive lookup. */
export function participanteCatalogNombreNormalizado(nombre: string): string {
  return foldParticipanteBusquedaText(nombre);
}

/** Inserts or updates a master-catalog participant row; always maintains `nombre_normalizado`. */
export function upsertParticipanteCatalogRow(
  db: Database,
  existingId: number | null,
  fields: ParticipanteCatalogUpsertFields,
  bind: (params: SqlJsBind[]) => SqlJsBind[],
): void {
  const nombreNormalizado = participanteCatalogNombreNormalizado(fields.nombre);
  if (existingId != null) {
    db.run(
      `UPDATE participantes SET tipo_documento_id = ?, documento = ?, nombre = ?, nombre_normalizado = ?, telefono = ?, celular = ?,
         comuna_id = ?, tipo_bono_id = ?, estado = ?,
         fecha_actualizacion = datetime('now') WHERE id = ?`,
      bind([
        fields.tipoDocumentoId,
        fields.documento,
        fields.nombre,
        nombreNormalizado,
        fields.telefono,
        fields.celular,
        fields.comunaId,
        fields.tipoBonoId,
        fields.estado,
        existingId,
      ]),
    );
    return;
  }

  db.run(
    `INSERT INTO participantes (
       public_id, tipo_documento_id, documento, nombre, nombre_normalizado, telefono, celular, comuna_id, tipo_bono_id,
       estado, fecha_creacion, fecha_actualizacion
     ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, datetime('now'), datetime('now'))`,
    bind([
      fields.publicId,
      fields.tipoDocumentoId,
      fields.documento,
      fields.nombre,
      nombreNormalizado,
      fields.telefono,
      fields.celular,
      fields.comunaId,
      fields.tipoBonoId,
      fields.estado,
    ]),
  );
}

/** Repairs stale or empty `nombre_normalizado` values from current `nombre` (seeds, legacy rows). */
export function backfillParticipantesNombreNormalizadoInDb(db: Database): number {
  const rows = queryAll(db, 'SELECT id, nombre, nombre_normalizado FROM participantes', []);
  let count = 0;
  for (const row of rows) {
    const id = Number(row.id);
    if (!Number.isFinite(id)) continue;
    const expected = participanteCatalogNombreNormalizado(String(row.nombre ?? ''));
    if (String(row.nombre_normalizado ?? '') !== expected) {
      db.run('UPDATE participantes SET nombre_normalizado = ? WHERE id = ?', [expected, id]);
      count += 1;
    }
  }
  return count;
}
